import React, { useEffect, useState } from "react";
import { Modal, Row, Col, Form } from "react-bootstrap";
import {
  Button,
  Box,
  Divider,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Formik } from "formik";
import * as Yup from "yup";
import { API } from "../../../api/API";
import { toast } from "react-toastify";
import {
  Business,
  Email,
  Phone,
  Home,
  Badge,
  Person,
} from "@mui/icons-material";

const validationSchema = Yup.object().shape({
  intitule: Yup.string().required("L'intitulé est obligatoire"),
  is_active: Yup.boolean(),
  email: Yup.string()
    .email("Email invalide")
    .required("L'email est obligatoire"),
  tel: Yup.string()
    .required("Le téléphone est obligatoire")
    .matches(/^[0-9]+$/, "Doit contenir uniquement des chiffres")
    .min(8, "Doit contenir au moins 8 chiffres"),
  adresse: Yup.string().required("L'adresse est obligatoire"),
  ifu: Yup.string().required("L'IFU est obligatoire"),
  rccm: Yup.string().required("Le RCCM est obligatoire"),
  numero: Yup.string().required("Le numéro est obligatoire"),
  type: Yup.string().required("Le type est obligatoire"),
  responsable_full_name: Yup.string(),
});

const PARTENAIRE_TYPES = [
  { value: "clinique", label: "Clinique" },
  { value: "laboratoire", label: "Laboratoire" },
  { value: "pharmacy", label: "Pharmacie" },
  { value: "clinique_laboratoire", label: "Clinique et Laboratoire" },
];

const UpdatePartenaireModal = ({
  show,
  handleClose,
  partenaire,
  onUpdateSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    intitule: "",
    is_active: false,
    email: "",
    tel: "",
    adresse: "",
    ifu: "",
    rccm: "",
    numero: "",
    date_creation: null,
    type: "",
    responsable_full_name: "",
    image: "",
    responsable_tel: "",
    responsable_email: "",
  });

  useEffect(() => {
    if (partenaire) {
      setInitialValues({
        intitule: partenaire.intitule || "",
        is_active: partenaire.is_active || false,
        email: partenaire.email || "",
        tel: partenaire.tel || "",
        adresse: partenaire.adresse || "",
        ifu: partenaire.ifu || "",
        rccm: partenaire.rccm || "",
        numero: partenaire.numero || "",
        date_creation: partenaire.date_creation
          ? dayjs(partenaire.date_creation)
          : null,
        type: partenaire.type || "",
        responsable_full_name: partenaire.responsable_full_name || "",
        image: partenaire.image || "",
        responsable_tel: partenaire.responsable_tel || "",
        responsable_email: partenaire.responsable_email || "",
      });
    }
  }, [partenaire]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formattedValues = {
        ...values,
      };

      await API.put(`/partenaires/${partenaire.id}`, formattedValues);
      toast.success("Partenaire mis à jour avec succès");
      onUpdateSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error updating partenaire:", error);
      toast.error(
        error.response?.data?.message || "Erreur lors de la mise à jour"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!partenaire) return null;

  return (
    <Modal
      size="xl"
      show={show}
      onHide={handleClose}
      aria-labelledby="update-partenaire-modal"
      centered
      backdrop="static"
      className="mt-4 "
    >
      <Paper
        sx={{
          p: 3,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Modal.Header closeButton sx={{ borderBottom: "none", pb: 0 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={initialValues.image} sx={{ width: 56, height: 56 }}>
              <Business fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Modifier{" "}
                <span className="text-success ">{initialValues.intitule}</span>
              </Typography>
              <Chip
                label={initialValues.is_active ? "Actif" : "Inactif"}
                color={initialValues.is_active ? "success" : "error"}
                size="small"
              />
            </Box>
          </Box>
        </Modal.Header>

        <Modal.Body style={{ overflowY: "auto", padding: "16px 0" }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
              isSubmitting,
              handleBlur,
              handleSubmit,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="formIntitule" className="mb-3">
                      <Form.Label>Intitulé *</Form.Label>
                      <Form.Control
                        name="intitule"
                        value={values.intitule}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.intitule && !!errors.intitule}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.intitule}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="formType" className="mb-3">
                      <Form.Label>Type *</Form.Label>
                      <Form.Select
                        name="type"
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.type && !!errors.type}
                      >
                        <option value="">Sélectionnez un type</option>
                        {PARTENAIRE_TYPES.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.type}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Divider sx={{ my: 2 }} />

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="formEmail" className="mb-3">
                      <Form.Label>
                        <Email
                          fontSize="small"
                          sx={{ mr: 1, verticalAlign: "middle" }}
                        />
                        Email *
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="formTel" className="mb-3">
                      <Form.Label>
                        <Phone
                          fontSize="small"
                          sx={{ mr: 1, verticalAlign: "middle" }}
                        />
                        Téléphone *
                      </Form.Label>
                      <Form.Control
                        name="tel"
                        value={values.tel}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.tel && !!errors.tel}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.tel}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group controlId="formAdresse" className="mb-3">
                  <Form.Label>
                    <Home
                      fontSize="small"
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Adresse *
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="adresse"
                    value={values.adresse}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.adresse && !!errors.adresse}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.adresse}
                  </Form.Control.Feedback>
                </Form.Group>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <Badge
                    fontSize="small"
                    sx={{ mr: 1, verticalAlign: "middle" }}
                  />
                  Informations légales
                </Typography>

                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group controlId="formIfu" className="mb-3">
                      <Form.Label>IFU *</Form.Label>
                      <Form.Control
                        name="ifu"
                        value={values.ifu}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.ifu && !!errors.ifu}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.ifu}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formRccm" className="mb-3">
                      <Form.Label>RCCM *</Form.Label>
                      <Form.Control
                        name="rccm"
                        value={values.rccm}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.rccm && !!errors.rccm}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.rccm}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formNumero" className="mb-3">
                      <Form.Label>Numéro *</Form.Label>
                      <Form.Control
                        name="numero"
                        value={values.numero}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.numero && !!errors.numero}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.numero}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="formIsActive" className="mb-3">
                      <Form.Label>Statut</Form.Label>
                      <div>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.is_active}
                              onChange={(e) =>
                                setFieldValue("is_active", e.target.checked)
                              }
                              color="primary"
                            />
                          }
                          label={values.is_active ? "Actif" : "Inactif"}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/*   <Divider sx={{ my: 2 }}> Infos Responsable</Divider>

                <Form.Group controlId="formResponsable" className="mb-3">
                  <Form.Label>
                    <Person
                      fontSize="small"
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Responsable *
                  </Form.Label>
                  <Form.Control
                    name="responsable_full_name"
                    value={values.responsable_full_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.responsable_full_name &&
                      !!errors.responsable_full_name
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.responsable_full_name}
                  </Form.Control.Feedback>
                </Form.Group>*/}

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    color="success"
                    startIcon={
                      isSubmitting ? <CircularProgress size={20} /> : null
                    }
                  >
                    {isSubmitting ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Paper>
    </Modal>
  );
};

export default UpdatePartenaireModal;
